import moment from "moment-timezone";
import {nearestFutureMinutes, updateObjectInArray} from "@/util";

const SERVICE_MINUTES_INTERVAL = 15;

const updateServiceTodayWithNearestIsoStart = function (serviceToday, nextServiceMoment) {
    return {
        ...serviceToday, iso: {
            ...serviceToday.iso, start: nextServiceMoment.format("YYYY-MM-DDTHH:mm:ss"),
        },
    };
};

const formatServiceDate = (serviceDate, startTime, endTime) => {
    return {
        display: getDisplayDate(serviceDate), iso: toISOString(serviceDate), times: [{start: startTime, end: endTime}],
    };
};

const formatAndConcatTimeSlots = (times) => {
    const slot1 = formatTimesSlot(times[0]);
    return times[1] ? slot1.concat(formatTimesSlot(times[1])) : slot1;
};

const formatTimesSlot = ({start, end}) => {
    const momentsArray = [];
    const nextServiceMoment = nearestFutureMinutes(SERVICE_MINUTES_INTERVAL, start);

    while (nextServiceMoment.isSameOrBefore(end)) {
        momentsArray.push({
            display: nextServiceMoment.format("LT"), time: nextServiceMoment.format("HH:mm"),
        });
        nextServiceMoment.add(SERVICE_MINUTES_INTERVAL, "minutes");
    }
    return momentsArray;
};

const formatTwoTimeSlotServiceDate = (serviceDate, timeSlot1, timeSlot2) => {
    return {
        display: getDisplayDate(serviceDate), iso: toISOString(serviceDate), times: [timeSlot1, timeSlot2],
    };
};

const getDisplayDate = (date) => {
    const mom = moment();
    const today = moment(mom.format("YYYY-MM-DD")).isSame(date);
    const tomorrow = moment(mom.add(1, "day").format("YYYY-MM-DD")).isSame(date);

    return today || tomorrow ? today ? "Today" : "Tomorrow" : moment(date).format("ddd, MMMM D");
};

const getEndTime = ({start, end}) => {
    /*
     *
     * if end same day as start then end stays as is
     * if end is tomorrow, end is last moment of current
     * service (pickup or delivery) day
     *
     */
    const endToday = end.clone().isSame(start, "day");
    return endToday ? end : lastMomentOfDay(start);
};

const lastMomentOfDay = (day) => {
    return day.clone().endOf("day");
};

const momentizeServiceDays = (serviceDays) => {
    return serviceDays.map((serviceDay) => {
        return {
            ...serviceDay, date: moment(serviceDay.date), iso: {
                start: moment(serviceDay.iso.start), end: moment(serviceDay.iso.end),
            },
        };
    });
};

const toISOString = (moment) => {
    return moment.format("YYYY-MM-DD");
};

function storeHoursRemaining(storeInfo) {
    // today is removed in mutation if no hours available
    const iso = moment().tz(storeInfo.tz).format("YYYY-MM-DD");
    return storeInfo.serviceDays.some((day) => day.iso === iso);
}

function nowIsAfterStoreStartMoment(storeInfo) {
    const now = moment().tz(storeInfo.tz);
    return now.isSameOrAfter(storeInfo.storeStartMoment, "minute");
}

function modifyTodayForCurrentMoment (currentMoment, serviceDays, serviceNow, minimumMinutesToFirstServiceTime) {
    const isoToday = currentMoment.format("YYYY-MM-DD");
    const serviceToday = serviceDays[0];
    const storesStartingServiceMoment = moment(serviceToday.iso.start);
    const currentMomentIsAfterStoreOpening = currentMoment.isSameOrAfter(storesStartingServiceMoment, "minute");

    const storesEarliestStartMomentToday = currentMomentIsAfterStoreOpening ? currentMoment.add(minimumMinutesToFirstServiceTime, "minutes") : storesStartingServiceMoment.add(minimumMinutesToFirstServiceTime, "minutes");

    const nextServiceMoment = serviceNow ? storesEarliestStartMomentToday : nearestFutureMinutes(SERVICE_MINUTES_INTERVAL, storesEarliestStartMomentToday);

    const storesEndingServiceMoment = moment(serviceToday.iso.end);

    const nextServiceMomentIsBeforeStoreClosing = nextServiceMoment.isSameOrBefore(storesEndingServiceMoment, "minute");

    return nextServiceMomentIsBeforeStoreClosing ? updateObjectInArray(serviceDays, updateServiceTodayWithNearestIsoStart(serviceToday, nextServiceMoment), "date") : serviceDays.filter((day) => day.date !== isoToday);
}

function formatServiceDates(serviceDays, waitTime) {
    /*
     *
     * API is sending hours past the end of day, for example 8-4 and 8:00am - 4:00am.
     * obviously the hours from 12-4 are not actually on that day. frontend needs to put
     * those hours on the following day to allow accurate online ordering as well as handle
     * overlapping hours from one day to another.
     *
     */

    const momentizedServiceDays = momentizeServiceDays(serviceDays);
    const days = [];

    momentizedServiceDays.forEach((serviceDay, i, momentizedServiceDays) => {
        const yesterdayDate = serviceDay.date.clone().subtract(1, "days");
        const yesterday = momentizedServiceDays.find(({date}) => date.isSame(yesterdayDate));

        const startOfDay = serviceDay.iso.start.clone().startOf("day");

        if (!yesterday) {
            const serviceDayIsToday = i === 0 && moment().isSame(serviceDay.date, "day");
            /*
             * waitTime already added to today, and, if today (which can only be the first serviceDay),
             * it does not have a yesterday in api response
             */
            const startTime = serviceDayIsToday ? serviceDay.iso.start : serviceDay.iso.start.add(waitTime, "minutes");

            const endTime = getEndTime(serviceDay.iso);
            days.push(formatServiceDate(serviceDay.date, startTime, endTime));
        } else {
            const overageYesterday = yesterday.iso.end.isSame(serviceDay.iso.start, "day");
            if (!overageYesterday) {
                const startTime = serviceDay.iso.start.add(waitTime, "minutes");

                const endTime = getEndTime(serviceDay.iso);
                days.push(formatServiceDate(serviceDay.date, startTime, endTime));
            } else {
                // yesterday end is same day as current day
                const overlap = yesterday.iso.end
                    .clone()
                    .isBetween(serviceDay.iso.start, serviceDay.iso.end);
                if (overlap) {
                    /*
                     * if overlap of yesterday and current service (pickup or delivery)
                     * day (yesterday's end time falls between start and end of current service day)
                     *
                     */
                    const endTime = getEndTime(serviceDay.iso);
                    days.push(formatServiceDate(serviceDay.date, startOfDay, endTime));
                } else {
                    // no overlap
                    const yesterdayEndTimeIsAfterCurrentServiceDayEndTime = yesterday.iso.end.isAfter(serviceDay.iso.end);
                    if (yesterdayEndTimeIsAfterCurrentServiceDayEndTime) {
                        // yesterday's end time is after current service (pickup or delivery) day's end time
                        days.push(formatServiceDate(serviceDay.date, startOfDay, yesterday.iso.end));
                    } else {
                        // here we need to add the "prep" time to start to get accurate start time

                        const timeSlot1 = {start: startOfDay, end: yesterday.iso.end};
                        const timeSlot2 = {
                            start: serviceDay.iso.start.add(waitTime, "minutes"), end: getEndTime(serviceDay.iso),
                        };
                        days.push(formatTwoTimeSlotServiceDate(serviceDay.date, timeSlot1, timeSlot2));
                    }
                }
            } // end else overage
        }

        /*
         *
         * concerned about no tomorrow for all days with overage
         * and want it added last so we're at the bottom
         *
         */

        const tomorrowDate = serviceDay.date.clone().add(1, "days");
        const tomorrow = momentizedServiceDays.find(({date}) => date.isSame(tomorrowDate));
        const overageToday = serviceDay.iso.end.isSame(tomorrowDate, "day");
        const lastServiceDay = i === momentizedServiceDays.length - 1;

        if (!tomorrow && !lastServiceDay && overageToday) {
            const startTimeOfTomorrow = tomorrowDate.clone().startOf("day");
            const endTime = serviceDay.iso.end;
            days.push(formatServiceDate(tomorrowDate, startTimeOfTomorrow, endTime));
        }
    });

    return days.map((day) => ({
        ...day, times: formatAndConcatTimeSlots(day.times),
    }));
}

export {
    storeHoursRemaining,
    nowIsAfterStoreStartMoment,
    modifyTodayForCurrentMoment,
    formatServiceDates
}
